import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchExtLandingData } from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { Button } from "../components/Button";
import Loader from "../components/Loader";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { FaBolt, FaWallet } from "react-icons/fa";
import { MdDiscount } from "react-icons/md";

interface ExtLandingData {
  eligibility: boolean;
  leadDetails?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    occupancy?: string;
    monthly_income?: string;
    dob?: Date;
    marital_status?: string;
    no_of_children?: string;
  };
  goal?: string;
}

const ExtLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leadData, setLeadData] = useState<ExtLandingData | null>(null);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 250;
  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();
  const uid = searchParams.get("amj");

  // Sanitize and validate goal text
  const sanitizeGoal = (text: string): string => {
    // Remove any HTML tags and decode entities to prevent XSS
    const div = document.createElement("div");
    div.textContent = text;
    const sanitized = div.textContent || "";

    // Truncate to max length
    const MAX_GOAL_LENGTH = 250;
    return sanitized.slice(0, MAX_GOAL_LENGTH);
  };

  useEffect(() => {
    setIsVisible(false);
    return () => setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    if (leadData?.goal !== undefined) {
      setCharCount(leadData.goal.length);
    }
  }, [leadData?.goal]);

  useEffect(() => {
    const fetchData = async () => {
      if (!uid) {
        UIUtils.showMessageOnSnackBar("Invalid URL");
        navigate("/");
        setLoading(false);
        return;
      }

      try {
        const response = await fetchExtLandingData(uid);
        const data: ExtLandingData = response.data;

        if (data.eligibility && data.leadDetails) {
          if (data.leadDetails.dob) {
            data.leadDetails.dob = new Date(data.leadDetails.dob);
          }
          setLeadData(data);
        } else {
          // Redirect to organic signup
          navigate("/");
          return;
        }
      } catch (err) {
        UIUtils.showMessageOnSnackBar(
          "Something went wrong. Please try again later."
        );
        UIUtils.trackException("External landing fetch error", err);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uid, navigate]);

  if (loading) {
    return <Loader message="Loading..." />;
  }

  if (!leadData) return null;

  const handleGoalChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const sanitizedGoal = sanitizeGoal(e.target.value);
    if (sanitizedGoal.length <= MAX_CHARS) {
      setLeadData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          goal: sanitizedGoal,
        };
      });
    }
  };

  const handleSignupClick = () => {
    navigate("/", {
      state: {
        leadDetails: leadData.leadDetails,
        fromExtLanding: true,
        goal: leadData.goal,
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col items-center">
      {/* Main Content Section */}
      <div className="max-w-2xl w-full pt-2">
        {/* Hero Section */}
        <div className="bg-blue-50 rounded-xl pb-6 pt-4 px-4 mb-6 shadow-md">
          <h1 className="text-xl font-bold text-blue-700 mb-4">
            Your loan search didn’t find a match — see how Afforda can help.
          </h1>
          <p className="text-lg text-blue-600 mb-4">
            Afforda is 100% free - no subscriptions, no hidden fees.
          </p>
          <p className="mb-4">
            Tell us what you need. We will show you options to help you move
            forward.
          </p>
          <div className="mb-4">
            <div className="relative">
              <textarea
                value={leadData.goal || ""}
                onChange={handleGoalChange}
                placeholder="Please describe your financial challenge or goal."
                className={`form-input w-full h-32 mb-1 border rounded-md focus:ring-2 focus:ring-blue-400 focus:border-transparent ${
                  charCount > MAX_CHARS
                    ? "border-orange-600"
                    : "border-neutral-200"
                }`}
              />
              <div
                className={`text-xs ${
                  charCount > MAX_CHARS ? "text-orange-600" : "text-neutral-400"
                }`}
              >
                {MAX_CHARS - charCount} characters remaining
              </div>
            </div>
          </div>
          <Button
            text="Get started - it's free"
            onClick={handleSignupClick}
            type="button"
            className="w-full bg-blue-500 hover:bg-blue-600"
          />
        </div>

        {/* Benefits Section */}
        <div className="bg-neutral-50 rounded-xl pb-6 pt-4 px-4 mb-6 shadow-md">
          <h2 className="text-xl font-bold text-neutral-800 mb-4">
            Take action today
          </h2>
          <div className="bg-white rounded-lg pb-6 pt-4 px-4 shadow-sm border border-neutral-200">
            <p className="text-lg font-semibold text-neutral-800 mb-2">
              Check if you're eligible for benefits - £22.7 billion goes
              unclaimed every year.
            </p>
            <p className="text-neutral-600 mb-4">
              Complete our quick benefits check form to see what you may be
              entitled to.
            </p>
            <Button
              text="See what you may qualify for"
              onClick={handleSignupClick}
              type="button"
              className="w-full bg-orange-500 hover:bg-orange-600"
            />
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">How Afforda helps</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 border border-neutral-200 flex items-center">
              <div>
                <FaBolt className="w-8 h-8 text-blue-500 mr-2 lg:mr-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-600">
                  Quick benefits check
                </h4>
                <p>See what support you could claim in a few clicks.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-neutral-200 flex items-center">
              <div>
                <MdDiscount className="w-8 h-8 text-blue-500 mr-2 lg:mr-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-600">Instant savings</h4>
                <p>
                  Get exclusive discounts at your favourite high street stores.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-neutral-200 flex items-center">
              <div>
                <FaWallet className="w-6 h-6 text-blue-500 mr-2 lg:mr-6" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-600">Credit options</h4>
                <p>
                  We compare products from over 15 lenders to see if it's
                  possible to get you an offer.{" "}
                  <span className="font-bold">Representative 1333%APR</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtLanding;
